"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import WageRatesTable from "../components/wage-rates-table";
import WageRatesModal, { WageRateFormValues } from "../components/wage-rates-modal";
import WageRatesService, { WageRate, EMPLOYEE_ROLE_TYPE, ROLE_TYPE_LABELS } from "../services/wage-rates-service";
import { BranchService, Branch } from "../../branch/services/branch-service";
import { showToast } from "../../../components/custom/custom-toast";
import { isSuccess } from "../../../models/response/base-response";
import { useFetchData } from "../../../hooks/useFetch";
import { ApiConfig, API_USER_ROUTER } from "../../../services/api-config";

export const WageRatesIndexPage: React.FC = () => {
  const [rows, setRows] = useState<WageRate[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<WageRate | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<WageRate | null>(null);

  // Data for dropdowns
  const [employees, setEmployees] = useState<Array<{ id: number; name: string }>>([]);
  const [branches, setBranches] = useState<Array<{ id: number; name: string }>>([]);

  // Filters
  const [filterRoleType, setFilterRoleType] = useState<number>(0);

  // Services
  const { fetch: listWageRates, loading: loadingList } = WageRatesService.list();
  const { fetch: createWageRate, loading: creating } = WageRatesService.create();
  const { fetch: updateWageRate, loading: updating } = WageRatesService.update();
  const { fetch: deleteWageRate, loading: deleting } = WageRatesService.delete();

  const { fetch: listBranches } = BranchService.listActive();

  // Employee service
  const { request: listUsers } = useFetchData.Get<any>({
    projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
  });

  // Store refs to prevent infinite loops
  const listWageRatesRef = useRef(listWageRates);
  const listUsersRef = useRef(listUsers);
  const listBranchesRef = useRef(listBranches);

  useEffect(() => {
    listWageRatesRef.current = listWageRates;
    listUsersRef.current = listUsers;
    listBranchesRef.current = listBranches;
  }, [listWageRates, listUsers, listBranches]);

  // Load wage rates
  const load = useCallback(async () => {
    const params: any = {};
    if (filterRoleType > 0) {
      params.role_type = filterRoleType;
    }
    const res = await listWageRatesRef.current(params);
    if (res && isSuccess(res)) {
      // Handle nested response: res.data may contain { status, message, data }
      const responseData = res.data;
      const actualData = responseData?.data ?? responseData;
      if (Array.isArray(actualData)) {
        setRows(actualData);
      } else {
        setRows([]);
      }
    }
  }, [filterRoleType]);

  // Load dropdown data
  const loadDropdownData = useCallback(async () => {
    // Load employees
    const userRes = await listUsersRef.current({
      endPoint: API_USER_ROUTER.API_LIST,
      data: { limit: 500, status: 1, company_id: 2000 },
    });
    if (userRes && isSuccess(userRes)) {
      const data = userRes.data;
      if (Array.isArray(data)) {
        setEmployees(data.map((u: any) => ({ id: u.id, name: u.name })));
      }
    }

    // Load branches
    const branchRes = await listBranchesRef.current();
    if (branchRes && isSuccess(branchRes)) {
      const data = branchRes.data;
      if (Array.isArray(data)) {
        setBranches(data.map((b: Branch) => ({ id: b.id, name: b.name })));
      }
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadDropdownData();
  }, [loadDropdownData]);

  // Handlers
  const handleCreate = async (values: WageRateFormValues): Promise<boolean> => {
    const res = await createWageRate({
      employee_id: values.employee_id,
      branch_id: values.branch_id,
      role_type: values.role_type,
      rate_per_kg: values.rate_per_kg,
      effective_from: values.effective_from,
      effective_to: values.effective_to,
      note: values.note,
    });
    if (res && isSuccess(res)) {
      showToast({ message: "Thêm đơn giá thành công", type: "success" });
      void load();
      return true;
    }
    showToast({ message: res?.message ?? "Không thể thêm đơn giá", type: "error" });
    return false;
  };

  const handleUpdate = async (values: WageRateFormValues): Promise<boolean> => {
    if (!editData) return false;
    const res = await updateWageRate(editData.id, {
      rate_per_kg: values.rate_per_kg,
      effective_to: values.effective_to,
      note: values.note,
    });
    if (res && isSuccess(res)) {
      showToast({ message: "Cập nhật đơn giá thành công", type: "success" });
      setEditData(null);
      void load();
      return true;
    }
    showToast({ message: res?.message ?? "Không thể cập nhật đơn giá", type: "error" });
    return false;
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    const res = await deleteWageRate(deleteConfirm.id);
    if (res && isSuccess(res)) {
      showToast({ message: "Xóa đơn giá thành công", type: "success" });
      setDeleteConfirm(null);
      void load();
      return;
    }
    showToast({ message: res?.message ?? "Không thể xóa đơn giá", type: "error" });
    setDeleteConfirm(null);
  };

  return (
    <div className="">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-semibold text-green-600 pl-3">
          Cấu hình đơn giá nhân viên
        </h1>
        <button
          onClick={() => {
            setEditData(null);
            setModalOpen(true);
          }}
          className="group inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-green-200 transition-all hover:bg-green-700 hover:-translate-y-0.5"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 transition-transform group-hover:rotate-90">
            <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
          </svg>
          <span>THÊM ĐƠN GIÁ</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-slate-50 rounded-lg p-4 mb-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-600">Loại công việc:</label>
            <select
              value={filterRoleType}
              onChange={(e) => setFilterRoleType(Number(e.target.value))}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm min-w-[150px]"
            >
              <option value={0}>Tất cả</option>
              {[
                EMPLOYEE_ROLE_TYPE.PICKUP,
                EMPLOYEE_ROLE_TYPE.LAUNDRY,
                EMPLOYEE_ROLE_TYPE.DELIVERY,
                EMPLOYEE_ROLE_TYPE.WASHER,
                EMPLOYEE_ROLE_TYPE.DRYER,
                EMPLOYEE_ROLE_TYPE.IRONER,
                EMPLOYEE_ROLE_TYPE.FOLDER,
              ].map((type) => (
                <option key={type} value={type}>
                  {ROLE_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <h4 className="text-sm font-semibold text-blue-800 mb-2">Hướng dẫn:</h4>
        <ul className="text-sm text-blue-700 list-disc list-inside space-y-1">
          <li>Đơn giá được áp dụng theo thứ tự ưu tiên: Nhân viên + Chi nhánh &gt; Nhân viên &gt; Chi nhánh &gt; Mặc định</li>
          <li>Để trống nhân viên/chi nhánh nghĩa là áp dụng cho tất cả</li>
          <li>Công nhân viên = Số ký x Đơn giá</li>
        </ul>
      </div>

      {/* Table */}
      <WageRatesTable
        rows={rows}
        isLoading={loadingList}
        onEdit={(row) => {
          setEditData(row);
          setModalOpen(true);
        }}
        onDelete={(row) => setDeleteConfirm(row)}
      />

      {/* Create/Edit Modal */}
      <WageRatesModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditData(null);
        }}
        onSubmit={editData ? handleUpdate : handleCreate}
        isSubmitting={creating || updating}
        editData={editData}
        employees={employees}
        branches={branches}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-[400px] shadow-2xl">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Xác nhận xóa</h3>
            <p className="text-slate-600 mb-6">
              Bạn có chắc muốn xóa đơn giá{" "}
              <strong>{ROLE_TYPE_LABELS[deleteConfirm.role_type]}</strong>
              {deleteConfirm.employee?.name && ` của ${deleteConfirm.employee.name}`}?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-100"
              >
                Hủy
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? "Đang xóa..." : "Xóa"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WageRatesIndexPage;
